import React, { memo, useEffect, useRef, useState } from "react";
import mapboxgl from "mapbox-gl";
import debounce from "lodash.debounce";
import "@mapbox/mapbox-gl-geocoder/dist/mapbox-gl-geocoder.css";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  IconButton,
  Typography,
  Alert,
  AlertTitle,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { Close, ExpandMore } from "@mui/icons-material";
import { useFormContext } from "react-hook-form";
import { useDynamicWatches } from "../../../../hooks/useDynamicWatches";
import {
  DEFAULT_MAP_BASEMAP,
  DEFAULT_MAP_CENTER,
} from "../../../../pages/publicMap/constants";
import {
  fetchElevation,
  isValidLatLon,
  parseNumberInput,
} from "../../../lib/utils";
import RulerControl from "@mapbox-controls/ruler";
import MapboxGeocoder from "@mapbox/mapbox-gl-geocoder";
import { coordinatesGeocoder } from "../../../../pages/publicMap/hooks/useMap/mapUtils";
import MarkerControl from "./MarkerControl";
import ZonesControl from "./ZonesControl";
import {
  groundwaterConservationDistrictsFill,
  groundwaterConservationDistrictsLine,
  groundwaterConservationDistrictsSymbol,
} from "./constants";

mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_TOKEN;

const Root = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  height: "100%",
  width: "100%",
  gap: 24,
  [theme.breakpoints.down("sm")]: {
    gap: 12,
  },
}));

const MapWrapper = styled(Box)({
  position: "relative",
  height: "100%",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
});

const MapContainer = styled(Box)({
  height: 325,
  width: "100%",
  position: "relative",
});

const StyledAlert = styled(Alert)(() => ({
  position: "absolute",
  width: "85%",
  maxWidth: 400,
  textAlign: "center",
  zIndex: 1000,
}));

const CloseButton = styled(IconButton)(() => ({
  position: "absolute",
  top: 4,
  right: 4,
}));

const toggleLayerVisibility = (map, layers, isVisible) => {
  if (map && layers.every((layer) => map.getLayer(layer))) {
    layers.forEach((layer) =>
      map.setLayoutProperty(layer, "visibility", isVisible ? "visible" : "none")
    );
  }
};

const LocationSelectionGuide = () => (
  <StyledAlert severity="info" variant="filled">
    <AlertTitle>Instructions</AlertTitle>
    <Box sx={{ textAlign: "left", lineHeight: 1.8 }}>
      • <strong>Click</strong> the map to set coordinates and elevation.
      <br />• <strong>Search</strong> for an address or lat/lng.
      <br />• <strong>Toggle marker</strong> placement and{" "}
      <strong>GCD boundaries</strong>.
      <br />• <strong>Zoom</strong> to your GPS location.
      <br />• <strong>Measure</strong> distances with the ruler tool.
    </Box>
    <CloseButton size="small">
      <Close fontSize="small" />
    </CloseButton>
  </StyledAlert>
);

const flyToCoords = (map, lon, lat) => {
  return new Promise((resolve) => {
    const mapHeight = map?.getContainer().clientHeight;
    const pixelOffset = 75;
    const offset = [0, mapHeight / 2 - pixelOffset];

    map.flyTo({
      center: [lon, lat],
      duration: 100,
      offset,
    });

    map.once("moveend", () => {
      resolve();
    });
  });
};

const VALUES_TO_WATCH = ["latitude", "longitude"];

const Index = () => {
  const { setValue, reset, watch } = useFormContext();

  const [map, setMap] = useState();
  const [markerEnabled, setMarkerEnabled] = useState(true);
  const [zonesVisible, setZonesVisible] = useState(false);
  const [instructionsOpen, setInstructionsOpen] = useState(true);
  const [marker, setMarker] = useState(null);
  const [clickedCoords, setClickedCoords] = useState(null);

  const mapContainerRef = useRef(null);
  const markerEnabledRef = useRef(markerEnabled);

  const watchedValues = useDynamicWatches(watch, VALUES_TO_WATCH);

  useEffect(() => {
    markerEnabledRef.current = markerEnabled;
  }, [markerEnabled]);

  //create map and apply all controls
  useEffect(() => {
    if (!mapContainerRef.current) return;

    const initialCenter =
      watchedValues["latitude"] && watchedValues["longitude"]
        ? [watchedValues["longitude"], watchedValues["latitude"]]
        : DEFAULT_MAP_CENTER;
    const initialZoom =
      watchedValues["latitude"] && watchedValues["longitude"] ? 16 : 8;

    const mapInstance = new mapboxgl.Map({
      container: mapContainerRef.current,
      style: DEFAULT_MAP_BASEMAP,
      center: initialCenter,
      zoom: initialZoom,
    });

    const mapHeight = mapInstance?.getContainer().clientHeight;
    const pixelOffset = 75;
    const offset = mapHeight / 2 - pixelOffset;

    mapInstance.flyTo({
      center: initialCenter,
      zoom: initialZoom,
      offset: [0, offset],
      duration: 0,
    });

    const markerInstance = new mapboxgl.Marker({ color: "#ff0000" })
      .setLngLat(initialCenter)
      .addTo(mapInstance);

    markerInstance.getElement().style.visibility = "hidden";

    mapInstance.on("click", async (e) => {
      if (!markerEnabledRef.current) return;

      const { lng, lat } = e.lngLat;
      markerInstance.setLngLat([lng, lat]);

      markerInstance.getElement().style.visibility = "visible";

      await flyToCoords(mapInstance, lng, lat);

      setClickedCoords({ lng, lat });
    });

    setMarker(markerInstance);

    mapInstance?.addControl(new mapboxgl.FullscreenControl(), "top-left");
    mapInstance?.addControl(
      new mapboxgl.GeolocateControl({
        positionOptions: { enableHighAccuracy: true },
        trackUserLocation: true,
        showUserHeading: true,
      }),
      "top-left"
    );
    mapInstance.addControl(
      new mapboxgl.ScaleControl({ unit: "imperial" }),
      "bottom-left"
    );
    mapInstance.addControl(
      new RulerControl({
        units: "yards",
        labelFormat: (n) => `${n.toFixed(2)} yd`,
      }),
      "bottom-right"
    );
    mapInstance.addControl(
      new MapboxGeocoder({
        accessToken: mapboxgl.accessToken,
        localGeocoder: coordinatesGeocoder,
        container: "geocoder-container",
        zoom: 16,
        mapboxgl,
        reverseGeocode: true,
        placeholder: "Address/Coords",
      }),
      "top-right"
    );

    mapInstance.on("load", () => {
      setMap(mapInstance);
    });

    return () => mapInstance.remove();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  //resizes map when mapContainerRef dimensions changes (sidebar toggle)
  useEffect(() => {
    if (!map || !mapContainerRef.current) return;

    const handleResize = debounce(() => {
      try {
        map.resize();
      } catch (e) {
        console.warn("Map resize failed", e);
      }
    }, 100);

    const observer = new ResizeObserver(handleResize);
    observer.observe(mapContainerRef.current);

    return () => {
      observer.disconnect();
      handleResize.cancel();
    };
  }, [map]);

  useEffect(() => {
    if (!map || !mapContainerRef.current) return;

    if (!map?.getSource("groundwater-conservation-districts")) {
      map?.addSource("groundwater-conservation-districts", {
        type: "vector",
        url: "mapbox://haystrinity.63ho1xgu",
      });

      map?.addLayer(groundwaterConservationDistrictsFill);
      map?.addLayer(groundwaterConservationDistrictsLine);
      map?.addLayer(groundwaterConservationDistrictsSymbol);
    }
  }, [map]);

  useEffect(() => {
    if (!map || !marker || !clickedCoords) return;

    const updateLocation = async () => {
      const { lat, lng } = clickedCoords;

      setValue("longitude", parseNumberInput(lng.toFixed(6)), {
        shouldDirty: true,
        shouldValidate: true,
      });
      setValue("latitude", parseNumberInput(lat.toFixed(6)), {
        shouldDirty: true,
        shouldValidate: true,
      });

      //TODO update fetch function to use new API
      fetchElevation(lat, lng).then((elevation) => {
        setValue("ft_elevation", parseNumberInput(elevation), {
          shouldDirty: true,
          shouldValidate: true,
        });
      });
    };

    void updateLocation();
  }, [clickedCoords, reset, map, marker, setValue]);

  useEffect(() => {
    if (!map || !marker) return;

    const lat = watchedValues["latitude"];
    const lon = watchedValues["longitude"];
    const { isValid } = isValidLatLon(lat, lon);
    const mapHeight = map?.getContainer().clientHeight;
    const pixelOffset = 75;
    const offset = mapHeight / 2 - pixelOffset;

    if (isValid) {
      marker.getElement().style.visibility = "visible";
      marker.setLngLat([lon, lat]);

      map?.flyTo({
        center: [lon, lat],
        duration: 0,
        offset: [0, offset],
      });
    } else {
      setInstructionsOpen(true);
      marker.getElement().style.visibility = "hidden";

      map?.flyTo({
        center: DEFAULT_MAP_CENTER,
        zoom: 8,
        duration: 0,
        offset: [0, offset],
      });
    }
  }, [watchedValues, map, marker]);

  useEffect(() => {
    toggleLayerVisibility(
      map,
      [
        "groundwater-conservation-districts-fill",
        "groundwater-conservation-districts-line",
        "groundwater-conservation-districts-symbol",
      ],
      zonesVisible
    );
  }, [map, zonesVisible]);

  return (
    <Accordion
      defaultExpanded
      sx={{
        border: "solid 1px rgba(0, 0, 0, 0.23)",
        borderRadius: "4px",
      }}
    >
      <AccordionSummary expandIcon={<ExpandMore />}>
        <Typography sx={{ fontWeight: 600 }}>
          Select Coordinates & Elevation Map
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Root>
          <MapWrapper
            component="section"
            onClick={() => instructionsOpen && setInstructionsOpen(false)}
          >
            <MapContainer ref={mapContainerRef} id="map" tabIndex={-1} />

            <ZonesControl
              open={zonesVisible}
              onToggle={() => setZonesVisible((prev) => !prev)}
              top={50}
            />

            <MarkerControl
              open={markerEnabled}
              onToggle={() => setMarkerEnabled((prev) => !prev)}
              top={90}
              title="Marker Placement"
            />
            {instructionsOpen && <LocationSelectionGuide />}
          </MapWrapper>
        </Root>
      </AccordionDetails>
    </Accordion>
  );
};
export default memo(Index);
